import React, { createElement, useState, type ReactNode } from "react";
import { pdf, type DocumentProps } from "@react-pdf/renderer";
import DrawRequestIcon from "Assets/Img/DrawRequestIcon";
import Button from "../../../../../Components/Common/Button/Button";
import Select from "../../../../../Components/Common/Select/Select";
import ChipEmailInput from "../../../../../Components/Common/ChipEmailInput/ChipEmailInput";
import Card from "../Card";
import FormField from "../FormField";
import SectionHeader from "../SectionHeader";
import ResidentialEstimateNote from "../ResidentialEstimateNote";
import { ProgressReportPrefill } from "../../../../../hooks/useConstructionProgressReport";
import { formatDate } from "utils/Common/Date";
import ConstructionReportPdf, {
  type ConstructionReportPdfProps,
} from "../ConstructionReportPdf";

const deliveryOptions = [
  { label: "Email + In-platform notification", value: "email_platform" },
  { label: "In-platform only", value: "platform" },
  { label: "Email only", value: "email" },
];

const lockOptions = [
  { label: "Yes - lock for editing after publish", value: "true" },
  { label: "No - allow revisions", value: "false" },
];

interface Step8PreviewPublishProps {
  onToast: (message: string) => void;
  prefill?: ProgressReportPrefill;
  invoiceAmount?: string;
  invoiceDate?: string;
  invoiceNumber?: string;
  reportDate?: string;
  recommendation?: string;
  recommendationText?: string;
  signatureDate?: string;
  servicesRequested?: string;
  drawReviewBullets?: string;
  phaseNotes?: Record<string, string>;
  flagNarratives?: Record<string, string>;
  extraFlags?: Array<{
    id: string;
    phase?: string;
    itemLabel: string;
    itemName?: string;
    narrative: string;
  }>;
  scheduleNotes?: string;
  changeOrders?: string;
  bankItems?: string;
  attachmentsList?: string;
  payableTo?: string;
  mailTo?: string;
  deliveryMethod?: string;
  onDeliveryMethodChange?: (val: string) => void;
  lockAfterPublish?: string;
  onLockAfterPublishChange?: (val: string) => void;
  deliveryCc?: string[];
  onDeliveryCcChange?: (val: string[]) => void;
}

const Step8PreviewPublish = ({
  onToast,
  prefill,
  invoiceAmount = "",
  invoiceDate = "",
  invoiceNumber = "",
  reportDate = "",
  recommendation = "",
  recommendationText = "",
  signatureDate = "",
  servicesRequested = "",
  drawReviewBullets = "",
  phaseNotes = {},
  flagNarratives = {},
  extraFlags = [],
  scheduleNotes = "",
  changeOrders = "",
  bankItems = "",
  attachmentsList = "",
  payableTo = "",
  mailTo = "",
  deliveryMethod = "email_platform",
  onDeliveryMethodChange,
  lockAfterPublish = "true",
  onLockAfterPublishChange,
  deliveryCc = [],
  onDeliveryCcChange,
}: Step8PreviewPublishProps) => {
  const [pdfLoading, setPdfLoading] = useState(false);
  const isCommercial = prefill?.project?.type === "commercial";
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") ?? "{}");
    } catch {
      return {};
    }
  })();
  const sigName = storedUser?.companyName ?? "";
  const sigAddress = storedUser?.address ?? "";

  const handlePdfPreview = async () => {
    setPdfLoading(true);
    try {
      const props: ConstructionReportPdfProps = {
        prefill,
        invoiceAmount,
        invoiceDate,
        invoiceNumber,
        reportDate,
        recommendation,
        recommendationText,
        signatureDate,
        servicesRequested,
        drawReviewBullets,
        phaseNotes,
        flagNarratives,
        extraFlags,
        scheduleNotes,
        changeOrders,
        bankItems,
        attachmentsList,
        payableTo,
        mailTo,
        deliveryMethod,
        deliveryCc,
        lockAfterPublish,
        sigName,
        sigAddress,
        overallCscEstimation:
          prefill?.inspectionSnapshot?.overallCscEstimation ?? "",
      };
      const el = createElement(
        ConstructionReportPdf,
        props,
      ) as React.ReactElement<DocumentProps>;
      const blob = await pdf(el).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      onToast("Failed to generate PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const project = prefill?.project;
  const stakeholders = prefill?.stakeholders;
  const inspection = prefill?.inspection;
  const draw = prefill?.draw;

  const lenderName = stakeholders?.lender?.companyName ?? "";
  const lendingOfficer = stakeholders?.lendingOfficer
    ? `${stakeholders.lendingOfficer?.firstName ?? ""} ${stakeholders.lendingOfficer?.lastName ?? ""}`.trim()
    : "";
  const ownerName = stakeholders?.owner?.companyName ?? "";
  const gcName = stakeholders?.gc
    ? stakeholders.gc.companyName ||
      `${stakeholders.gc?.firstName ?? ""} ${stakeholders.gc?.lastName ?? ""}`.trim()
    : "";
  const reportNum = prefill?.nextReportNumber ?? "";
  const drawNum = draw?.drawNumber ?? "";
  const g702 = draw?.g702;
  const sym = g702?.originalContractSum?.currency?.symbol ?? "$";
  const fmt = (amount?: number) =>
    amount !== undefined
      ? `${sym}${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
      : "—";
  const drawNumPadded = drawNum ? String(drawNum).padStart(2, "0") : "";

  const deliverTo =
    [lendingOfficer, lenderName].filter(Boolean).join(` (`) +
    (lenderName ? ")" : "");

  const formattedInvoiceNum = invoiceNumber
    ? invoiceNumber.startsWith("#")
      ? invoiceNumber
      : `#${invoiceNumber}`
    : "";

  const forLine = servicesRequested || "—";
  const toLine = [lenderName, lendingOfficer].filter(Boolean).join(" · ");

  const previewSections: {
    title: string;
    body: ReactNode;
    callout?: boolean;
  }[] = [
    {
      title: `Invoice${formattedInvoiceNum ? ` ${formattedInvoiceNum}` : ""}`,
      body: (
        <>
          {toLine && <p>To: {toLine}</p>}
          {project?.name && (
            <p>
              Project: {project.name}
              {project.address ? ` · ${project.address}` : ""}
            </p>
          )}
          <p>
            For: {forLine}
            {invoiceAmount ? (
              <>
                {" "}
                · <strong>Total Due: ${invoiceAmount}</strong>
              </>
            ) : (
              ""
            )}
          </p>
        </>
      ),
    },
    {
      title: "Project Overview",
      body: (
        <>
          {(lenderName || lendingOfficer) && (
            <p>
              <strong>Bank / Lender:</strong>{" "}
              {[lenderName, lendingOfficer].filter(Boolean).join(" | ")}
            </p>
          )}
          {project?.name && (
            <p>
              <strong>Project:</strong>{" "}
              {[project.name, project.address].filter(Boolean).join(" | ")}
            </p>
          )}
          {project?.description && (
            <p>
              <strong>Description:</strong> {project.description}
            </p>
          )}
          {ownerName && (
            <p>
              <strong>Owner:</strong> {ownerName}
            </p>
          )}
          {gcName && (
            <p>
              <strong>Contractor:</strong> {gcName}
            </p>
          )}
          {forLine && (
            <p>
              <strong>Services:</strong> {forLine}
            </p>
          )}
          {inspection?.inspectionDate && (
            <p>
              <strong>Inspection Date:</strong> {inspection.inspectionDate}
              {reportNum ? (
                <>
                  {" "}
                  · <strong>Report {reportNum}</strong>
                </>
              ) : (
                ""
              )}
            </p>
          )}
        </>
      ),
    },
    {
      title: `Draw Summary${drawNumPadded ? ` — Application #${drawNumPadded}` : ""}`,
      body: (
        <>
          <p>
            {gcName || "The contractor"} have submitted Application for Payment{" "}
            {drawNumPadded || "—"} Period to{" "}
            {formatDate(invoiceDate, false) || "—"} in the amount of{" "}
            <strong>{fmt(g702?.currentPaymentDue?.amount)}</strong>.
          </p>
          <br />
          <p>
            Contract Sum To Date: {fmt(g702?.contractSumToDate?.amount)}{" "}
            &nbsp;·&nbsp; Total Completed To Date:{" "}
            {fmt(g702?.totalCompletedAndStored?.amount)} &nbsp;·&nbsp;
            Retainage: {fmt(g702?.totalRetainage?.amount)} &nbsp;·&nbsp; Total
            Earned Less Retainage: {fmt(g702?.totalEarnedLessRetainage?.amount)}{" "}
            &nbsp;·&nbsp; Less Previous Certificates:{" "}
            {fmt(g702?.lessPreviousCertificates?.amount)} &nbsp;·&nbsp;
            <strong>
              Current Payment Due: {fmt(g702?.currentPaymentDue?.amount)}
            </strong>{" "}
            &nbsp;·&nbsp; Balance to Finish:{" "}
            {fmt(g702?.balanceToFinish?.amount)}
          </p>
        </>
      ),
    },
    {
      title: "Draw Review",
      body: drawReviewBullets ? (
        <ul className="drawReviewBullets">
          {drawReviewBullets
            .split("\n")
            .map((line) => line.replace(/^•\s*/, "").trim())
            .filter(Boolean)
            .map((line, i) => (
              <li key={i}>{line}</li>
            ))}
        </ul>
      ) : (
        "—"
      ),
    },
    {
      title: "Progress Report",
      body: (() => {
        const snapshot = prefill?.inspectionSnapshot;
        const allItems: Array<{
          id: string;
          itemLabel: string;
          note?: Record<string, string>;
        }> = snapshot?.phases
          ? snapshot.phases.flatMap((p) => p.items)
          : (snapshot?.lineItems ?? []).map((li) => ({
              id: li.id,
              itemLabel: li.aiaItem.itemName,
              note: li.note,
            }));
        const noteItems = allItems.filter((item) => {
          const hasNote = (phaseNotes[item.id] ?? "")
            .replace(/^•\s*/, "")
            .trim();
          const hasFlag = (flagNarratives[item.id] ?? "").trim();
          return hasNote || hasFlag;
        });
        const extraFlagsFilled = extraFlags.filter((f) => f.narrative.trim());
        const hasContent = noteItems.length > 0 || extraFlagsFilled.length > 0;
        if (!hasContent) return <span>—</span>;
        return (
          <ul className="drawReviewBullets">
            {noteItems.map((item) => {
              const flagNarrative = (flagNarratives[item.id] ?? "").trim();
              const lines = (phaseNotes[item.id] ?? "")
                .split("\n")
                .map((l) => l.replace(/^•\s*/, "").trim())
                .filter(Boolean);
              return (
                <li key={item.id}>
                  {item.itemLabel}:{" "}
                  {lines.map((line, i) => (
                    <span key={i}>
                      {i > 0 ? ". " : ""}
                      {line}
                    </span>
                  ))}
                  {flagNarrative && (
                    <span style={{ color: "#c0392b" }}>
                      {lines.length > 0 ? ". " : ""}
                      {flagNarrative}
                    </span>
                  )}
                </li>
              );
            })}
            {extraFlagsFilled.map((flag) => (
              <li key={flag.id} style={{ color: "#c0392b" }}>
                {flag.itemName ?? flag.itemLabel}: {flag.narrative.trim()}
              </li>
            ))}
            <ResidentialEstimateNote isCommercial={isCommercial} />
          </ul>
        );
      })(),
    },
    {
      title: "Schedule",
      body: scheduleNotes ? (
        <ul className="drawReviewBullets">
          {scheduleNotes
            .split("\n")
            .map((line) => line.replace(/^•\s*/, "").trim())
            .filter(Boolean)
            .map((line, i) => (
              <li key={i}>{line}</li>
            ))}
        </ul>
      ) : (
        "—"
      ),
    },
    {
      title: "Change Orders",
      body: changeOrders || "—",
    },
    {
      title: "Items for Bank to Verify",
      body: bankItems || "—",
    },
    {
      title: "Information of Action Required",
      body:
        recommendationText ||
        "Pay the Draw as noted in this report upon Lender approval.",
      callout: true,
    },
    {
      title: "Attachments",
      body: attachmentsList || "—",
    },
    {
      title: "",
      body: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <span className="sigName">{sigName || "—"}</span>
            {sigAddress && <p className="sigAddress">{sigAddress}</p>}
          </div>
          <div style={{ textAlign: "right" }}>
            <p className="signature">{sigName}</p>
            <p className="sigAddress">
              {formatDate(signatureDate, false) || "—"}
            </p>
          </div>
        </div>
      ),
    },
  ];

  const headerTitle = [project?.name, project?.address]
    .filter(Boolean)
    .join(" - ");
  const headerMeta = [
    reportNum && `Report ${reportNum}`,
    formatDate(reportDate || invoiceDate || signatureDate, false),
    lenderName,
    lendingOfficer,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="craScreen">
      <SectionHeader
        title="Preview and Publish"
        subtitle={`Review compiled report before publishing${lenderName ? ` to ${lenderName}` : ""}.`}
      />

      <div className="previewActions">
        <Button
          className="btnSecondary"
          onClick={handlePdfPreview}
          disabled={pdfLoading}
        >
          <DrawRequestIcon /> {pdfLoading ? "Generating..." : "Preview as PDF"}
        </Button>
        <Button className="btnSecondary" onClick={() => onToast("Draft saved")}>
          Save Draft
        </Button>
      </div>

      <div className="previewPanel">
        <header>
          <small>Construction Progress Report</small>
          <h3>{headerTitle || "—"}</h3>
          <p>{headerMeta || "—"}</p>
        </header>
        <div className="previewPanel__body">
          {previewSections.map((section) => (
            <section
              key={section.title}
              className={
                "callout" in section && section.callout ? "callout" : ""
              }
            >
              <h4>{section.title}</h4>
              <div className="secItem">{section.body || "—"}</div>
            </section>
          ))}
        </div>
      </div>

      <Card title="Delivery Settings" className="mt-3">
        <div className="craFieldRow cols2">
          <FormField
            label="Deliver To"
            required
            value={deliverTo || ""}
            otherProps={{ disabled: true }}
          />
          <div className="craField overflow-auto">
            <label>CC</label>
            <ChipEmailInput
              value={deliveryCc}
              onChange={(val) => onDeliveryCcChange?.(val)}
            />
          </div>
        </div>
        <div className="craFieldRow cols2">
          <div className="craField">
            <label>Delivery Method</label>
            <Select
              options={deliveryOptions}
              value={deliveryMethod}
              onChange={(e) => onDeliveryMethodChange?.(e.target.value)}
              height={"38px"}
            />
          </div>
          <div className="craField">
            <label>Lock Report After Publishing</label>
            <Select
              options={lockOptions}
              value={lockAfterPublish}
              onChange={(e) => onLockAfterPublishChange?.(e.target.value)}
              height={"38px"}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Step8PreviewPublish;
